import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import axios from "axios";
import { API } from "@/config";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
    User, Phone, Mail, ArrowLeft, DollarSign, Calendar
} from "lucide-react";
import AvailabilityConfig from "@/components/AvailabilityConfig";
import { toPaise, toRupees } from "../utils/currency";
import { db } from "../db";
import PropTypes from 'prop-types';

export default function StaffDetail({ token, onLogout }) {
    const { clinicId, memberId } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    // Member state
    const [member, setMember] = useState(null);

    // Facility charge state
    const [commissionRule, setCommissionRule] = useState(null);
    const [isEditingCommission, setIsEditingCommission] = useState(false);
    const [commissionForm, setCommissionForm] = useState({
        rule_type: 'percent',
        fee_value: 0
    });

    // Availability state
    const [isEditingAvailability, setIsEditingAvailability] = useState(false);
    const [availabilityForm, setAvailabilityForm] = useState({
        availability: {},
        slot_duration_minutes: 30
    });

    const doctorId = localStorage.getItem('user_id');
    const myMembership = useLiveQuery(async () => {
        if (!clinicId || !doctorId) return undefined;
        const members = await db.clinic_members
            .where('[clinic_id+user_id]')
            .equals([clinicId, doctorId])
            .first();
        return members;
    }, [clinicId, doctorId]);
    const isAdmin = myMembership?.role === 'admin';
    // isSelf check for viewing own data
    // member state is set later, so we calculate specific checks in render or useMemo but doctorId is available here.

    // Fetch member details
    useEffect(() => {
        fetchMemberDetails();
    }, [clinicId, memberId]);

    // Fetch facility_charge rule if consultant
    useEffect(() => {
        if (member && member.role === 'consultant') {
            fetchCommissionRule();
        }
    }, [member]);

    const fetchMemberDetails = async () => {
        try {
            setIsLoading(true);
            // We reuse the list endpoint and filter
            const response = await axios.get(`${API}/clinics/${clinicId}/members`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const found = response.data.find(m => m.id === memberId);
            if (found) {
                setMember(found);
                setAvailabilityForm({
                    availability: found.availability || {},
                    slot_duration_minutes: found.slot_duration_minutes || 30,
                    consultation_fee: found.consultation_fee !== undefined && found.consultation_fee !== null ? toRupees(found.consultation_fee) : ""
                });
            } else {
                toast.error("Member not found");
                navigate(-1);
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to load member details");
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCommissionRule = async () => {
        try {
            // We need to find the rule for this doctor in this clinic
            // GET /api/fee-agreements?clinic_id=X&doctor_id=Y
            // Actually server.py endpoint: get_commission_rules(clinic_id=...) returns all for that doctor (the api key doctor)
            // Wait, the API `get_commission_rules` filters by `current_doctor["id"]`.
            // But I am the Admin. I want to see the rule for "Dr. Staff".
            // The API `get_commission_rules` in server.py:
            // query = {"doctor_id": current_doctor["id"]} -> This forces it to be MY rules (as treating doctor).
            // This endpoint is for "My Fee Agreements".

            // The Admin needs to see "Fee Agreements for this Clinic".
            // Implementation Gaps:
            // `get_commission_rules` in server.py (lines 1937-1954) seems restricted to `current_doctor["id"]`.
            // WE NEED TO FIX THIS in backend or use a different endpoint?
            // The user requirement implies we should be able to see it.
            // 
            // Let's check `server.py` again.
            // Line 1943: query = {"doctor_id": current_doctor["id"]}
            // This is definitely wrong for the Admin viewing staff rules case.
            // The Admin needs to query rules where `clinic_id` is their clinic?

            // For now, I'll assumne I need to fix the backend endpoint too (adding to task list implicitly or doing it now).
            // Actually I should fix it now.
            // But let's write the frontend assuming the endpoint works or I'll fix it.

            // Let's try querying by clinic_id.
            const response = await axios.get(`${API}/fee-agreements`, {
                params: { clinic_id: clinicId },
                headers: { Authorization: `Bearer ${token}` }
            });

            // The response list will contain rules for the clinic.
            // We need to find the one for THIS doctor.
            // The rule should have `doctor_id` equal to member.doctor_id.
            const rule = response.data.find(r => r.user_id === member.user_id);
            if (rule) {
                setCommissionRule(rule);
                setCommissionForm({
                    rule_type: rule.rule_type,
                    fee_value: rule.rule_type === 'flat'
                        ? toRupees(rule.fee_value)
                        : rule.fee_value
                });
            } else {
                setCommissionRule(null);
                // Default defaults
                setCommissionForm({ rule_type: 'percent', fee_value: 0 });
            }
        } catch (error) {
            console.error("Error fetching rules:", error);
        }
    };

    const handleSaveAvailability = async () => {
        try {
            setIsLoading(true);
            const payload = {
                availability: availabilityForm.availability,
                slot_duration_minutes: parseInt(availabilityForm.slot_duration_minutes) || 30,
            };

            if (availabilityForm.consultation_fee !== "" && availabilityForm.consultation_fee !== undefined) {
                payload.consultation_fee = toPaise(availabilityForm.consultation_fee);
            }

            await axios.put(`${API}/clinics/${clinicId}/members/${member.id}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Availability updated");
            setIsEditingAvailability(false);
            fetchMemberDetails();
        } catch (error) {
            toast.error("Failed to update availability");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveCommission = async () => {
        try {
            setIsLoading(true);
            if (commissionRule) {
                // Update existing
                await axios.put(`${API}/fee-agreements/${commissionRule.id}`, {
                    rule_type: commissionForm.rule_type,
                    fee_value: commissionForm.rule_type === 'flat'
                        ? toPaise(commissionForm.fee_value)
                        : parseFloat(commissionForm.fee_value)
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Fee agreement updated");
            } else {
                // Create new
                await axios.post(`${API}/fee-agreements`, {
                    clinic_id: clinicId,
                    user_id: member.user_id,
                    rule_type: commissionForm.rule_type,
                    fee_value: commissionForm.rule_type === 'flat'
                        ? toPaise(commissionForm.fee_value)
                        : parseFloat(commissionForm.fee_value),
                    status: 'active'
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success("Fee agreement created");
            }
            setIsEditingCommission(false);
            fetchCommissionRule();
        } catch (error) {
            toast.error("Failed to save facility_charge");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!member) {
        return (
            <div className="page-container">
                <Sidebar active="clinics" onLogout={onLogout} />
                <div className="main-content">
                    <div className="p-8 text-center text-slate-500">Loading...</div>
                </div>
            </div>
        );
    }



    return (
        <div className="page-container">
            <Sidebar active="clinics" onLogout={onLogout} />
            <div className="main-content">
                {/* Header */}
                <div className="header mb-6">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" onClick={() => navigate(`/clinics/${clinicId}`, { state: { activeTab: 'staff' } })} className="p-2">
                            <ArrowLeft size={20} />
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">{member.doctor_name || 'Staff Member'}</h1>
                            <p className="text-slate-500 flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize
                                    ${member.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                                        member.role === 'doctor' ? 'bg-emerald-100 text-emerald-800' :
                                            member.role === 'consultant' ? 'bg-indigo-100 text-indigo-800' :
                                                'bg-amber-100 text-amber-800'}`}>
                                    {member.role}
                                </span>
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid gap-6 max-w-3xl">
                    {/* Contact Info Card */}
                    <div className="card p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                            <User size={20} className="text-slate-400" />
                            Contact Information
                        </h3>
                        <div className="grid gap-4">
                            <div className="flex items-center gap-3 text-slate-600">
                                <Phone size={18} />
                                <span>{member.doctor_phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-3 text-slate-600">
                                <Mail size={18} />
                                <span>{member.doctor_email || 'N/A'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Online Booking Settings - Doctors and Consultants Only & Admin Only */}
                    {(member.role === 'doctor' || member.role === 'consultant') && isAdmin && (
                        <div className="card p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <Calendar size={20} className="text-blue-500" />
                                    Online Booking (Clinic Availability)
                                </h3>
                                {!isEditingAvailability ? (
                                    <Button variant="outline" size="sm" onClick={() => setIsEditingAvailability(true)}>
                                        Edit
                                    </Button>
                                ) : (
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setIsEditingAvailability(false);
                                                if (member) {
                                                    setAvailabilityForm({
                                                        availability: member.availability || {},
                                                        slot_duration_minutes: member.slot_duration_minutes || 30,
                                                        consultation_fee: member.consultation_fee !== undefined && member.consultation_fee !== null ? toRupees(member.consultation_fee) : ""
                                                    });
                                                }
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                        <Button size="sm" onClick={handleSaveAvailability} disabled={isLoading}>
                                            Save
                                        </Button>
                                    </div>
                                )}
                            </div>

                            {!isEditingAvailability ? (
                                <div className="space-y-4">
                                    <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                                        <Label className="text-blue-800 font-semibold mb-2 block">Clinic Booking Link for this Doctor</Label>
                                        <div className="flex gap-2">
                                            <Input
                                                readOnly
                                                value={`${window.location.origin}/book/clinic/${clinicId}?doctor_id=${member.user_id}`}
                                                className="bg-white border-blue-200 text-slate-700"
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="bg-white text-blue-700 hover:bg-blue-100 border-blue-200"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(`${window.location.origin}/book/clinic/${clinicId}?doctor_id=${member.user_id}`);
                                                    toast.success("Link copied!");
                                                }}
                                            >
                                                Copy
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                                        <div className="flex flex-col sm:flex-row gap-4 sm:gap-12 mb-4">
                                            <div>
                                                <div className="text-sm text-slate-500 mb-1">Fee per consultation</div>
                                                <div className="font-medium text-slate-900">{member.consultation_fee !== null && member.consultation_fee !== undefined ? `₹${toRupees(member.consultation_fee)}` : 'Not Set'}</div>
                                            </div>
                                            <div>
                                                <div className="text-sm text-slate-500 mb-1">Slot Duration</div>
                                                <div className="font-medium text-slate-900">{member.slot_duration_minutes || 30} minutes</div>
                                            </div>
                                        </div>

                                        <div className="text-sm text-slate-500 mb-2">Weekly Schedule</div>
                                        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
                                            {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                                                const slots = (member.availability || {})[day] || [];
                                                if (slots.length === 0) return null;
                                                return (
                                                    <div key={day} className="flex justify-between items-center text-sm py-1 border-b border-slate-200 last:border-0">
                                                        <span className="capitalize font-medium text-slate-700">{day}</span>
                                                        <span className="text-slate-600">
                                                            {slots.map((s, idx) => (
                                                                <span key={idx} className="bg-white px-2 py-0.5 rounded border border-slate-200 ml-1">
                                                                    {s.start} - {s.end}
                                                                </span>
                                                            ))}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                            {Object.values(member.availability || {}).flat().length === 0 && (
                                                <div className="text-slate-400 italic text-sm">No availability set</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-6 animate-in fade-in">
                                    <div>
                                        <Label className="mb-2 block">Slot Duration (Minutes)</Label>
                                        <div className="flex gap-2 items-center">
                                            <Input
                                                type="number"
                                                step="1"
                                                min="1"
                                                max="120"
                                                value={availabilityForm.slot_duration_minutes}
                                                onChange={(e) => setAvailabilityForm({ ...availabilityForm, slot_duration_minutes: e.target.value })}
                                                className="w-24"
                                            />
                                            <div className="flex gap-1">
                                                {[15, 30, 45, 60].map((mins) => (
                                                    <Button
                                                        key={mins}
                                                        type="button"
                                                        variant={parseInt(availabilityForm.slot_duration_minutes) === mins ? "default" : "outline"}
                                                        size="sm"
                                                        className="h-9 px-2 text-xs"
                                                        onClick={() => setAvailabilityForm({ ...availabilityForm, slot_duration_minutes: mins })}
                                                    >
                                                        {mins === 60 ? '1h' : `${mins}m`}
                                                    </Button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <Label className="mb-2 block">Consultation Fee (₹)</Label>
                                        <Input
                                            type="number"
                                            step="1"
                                            min="0"
                                            placeholder="e.g. 500"
                                            value={availabilityForm.consultation_fee}
                                            onChange={(e) => setAvailabilityForm({ ...availabilityForm, consultation_fee: e.target.value })}
                                            className="w-32 border-slate-300"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">This fee overrides your default fee for appointments booked at this clinic.</p>
                                    </div>

                                    <div>
                                        <Label className="mb-4 block">Weekly Availability</Label>
                                        <AvailabilityConfig
                                            value={availabilityForm.availability}
                                            onChange={(val) => setAvailabilityForm({ ...availabilityForm, availability: val })}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Facility Charge Settings - Consultants Only & Admin Viewer Only */}
                    {member.role === 'consultant' && (isAdmin || (doctorId === member.user_id)) && (
                        <div className="card p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold flex items-center gap-2">
                                    <DollarSign size={20} className="text-emerald-500" />
                                    Facility Charge Settings
                                </h3>
                                {isAdmin && (
                                    !isEditingCommission ? (
                                        <Button variant="outline" size="sm" onClick={() => setIsEditingCommission(true)}>
                                            Edit
                                        </Button>
                                    ) : (
                                        <div className="flex gap-2">
                                            <Button variant="ghost" size="sm" onClick={() => setIsEditingCommission(false)}>
                                                Cancel
                                            </Button>
                                            <Button size="sm" onClick={handleSaveCommission} disabled={isLoading}>
                                                Save
                                            </Button>
                                        </div>
                                    )
                                )}
                            </div>

                            {!isEditingCommission ? (
                                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                                    {commissionRule ? (
                                        <div className="flex flex-col gap-1">
                                            <div className="text-sm text-slate-500">Fee Agreement</div>
                                            <div className="text-xl font-semibold text-slate-900">
                                                {commissionRule.rule_type === 'percent'
                                                    ? `${commissionRule.fee_value}%`
                                                    : `₹${toRupees(commissionRule.fee_value)}`}
                                                <span className="text-sm font-normal text-slate-500 ml-1">
                                                    per appointment
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-slate-500 italic">
                                            No facility_charge rule set.
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-4 animate-in fade-in">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label>Type</Label>
                                            <Select
                                                value={commissionForm.rule_type}
                                                onValueChange={(v) => setCommissionForm({ ...commissionForm, rule_type: v })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="percent">Percentage (%)</SelectItem>
                                                    <SelectItem value="flat">Flat Amount (₹)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label>Value</Label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                placeholder={commissionForm.rule_type === "flat" ? "Amount" : "%"}
                                                value={commissionForm.fee_value}
                                                onChange={(e) => setCommissionForm({ ...commissionForm, fee_value: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-slate-500">
                                        Facility charge will be credited to the clinic admin for every appointment completed by this doctor.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

StaffDetail.propTypes = {
    token: PropTypes.string,
    onLogout: PropTypes.func
};
